import React, { useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import './FinancialCharts.css';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const CHART_TABS = [
    { id: 'trends', label: 'Trends' },
    { id: 'costs', label: 'Cost Breakdown' },
    { id: 'margins', label: 'Product Margins' },
];

const FinancialCharts = ({ chartData }) => {
    // 🔑 CRITICAL CHECK: Ensure chartData exists before attempting to access its properties.
    const data = chartData || {};
    const [activeTab, setActiveTab] = useState('trends');

    // --- Chart Data Memoization with Null/Empty Checks ---

    // Revenue Trend Chart Data
    const revenueChartData = useMemo(() => {
        // Check for the existence of revenueData object
        const revenueData = data.revenueData || {};
        return {
            // Check labels and values arrays
            labels: revenueData.labels || [],
            datasets: [
                {
                    label: 'Revenue',
                    data: revenueData.values || [],
                    borderColor: 'rgb(59, 130, 246)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    tension: 0.4,
                    fill: true,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                }
            ]
        };
    }, [data.revenueData]);

    // Profit Margin by Product Chart Data
    const profitMarginChartData = useMemo(() => {
        // Check for the existence and non-emptiness of the array
        const profitMarginByProduct = data.profitMarginByProduct?.length ? data.profitMarginByProduct : [];

        return {
            // Safely map over the checked array
            labels: profitMarginByProduct.map(item => item?.product).filter(Boolean),
            datasets: [
                {
                    label: 'Profit Margin (%)',
                    // Safely map over the checked array
                    data: profitMarginByProduct.map(item => item?.margin),
                    backgroundColor: [
                        'rgba(34, 197, 94, 0.8)',
                        'rgba(59, 130, 246, 0.8)',
                        'rgba(168, 85, 247, 0.8)',
                        'rgba(245, 158, 11, 0.8)',
                        'rgba(239, 68, 68, 0.8)',
                    ],
                    borderColor: [
                        'rgb(34, 197, 94)',
                        'rgb(59, 130, 246)',
                        'rgb(168, 85, 247)',
                        'rgb(245, 158, 11)',
                        'rgb(239, 68, 68)',
                    ],
                    borderWidth: 2,
                }
            ]
        };
    }, [data.profitMarginByProduct]);

    // Cost Breakdown Pie Chart Data
    const costBreakdownChartData = useMemo(() => {
        // Check for the existence of costBreakdown object
        const costBreakdown = data.costBreakdown || {};

        // Fallback values for data points if the object or its keys are missing
        const costData = [
            costBreakdown.ingredients || 0,
            costBreakdown.labor || 0,
            costBreakdown.overhead || 0,
            costBreakdown.packaging || 0,
        ];

        return {
            labels: ['Ingredients', 'Labor', 'Overhead', 'Packaging'],
            datasets: [
                {
                    label: 'Cost Distribution (%)',
                    data: costData,
                    backgroundColor: [
                        'rgba(239, 68, 68, 0.8)',
                        'rgba(245, 158, 11, 0.8)',
                        'rgba(59, 130, 246, 0.8)',
                        'rgba(34, 197, 94, 0.8)',
                    ],
                    borderColor: [
                        'rgb(239, 68, 68)',
                        'rgb(245, 158, 11)',
                        'rgb(59, 130, 246)',
                        'rgb(34, 197, 94)',
                    ],
                    borderWidth: 2,
                }
            ]
        };
    }, [data.costBreakdown]);

    // Monthly P&L Chart Data
    const monthlyPLChartData = useMemo(() => {
        // Check for the existence of monthlyProfitLoss object
        const monthlyProfitLoss = data.monthlyProfitLoss || {};

        return {
            // Check labels, revenue, costs, and profit arrays
            labels: monthlyProfitLoss.labels || [],
            datasets: [
                {
                    label: 'Revenue',
                    type: 'line',
                    data: monthlyProfitLoss.revenue || [],
                    borderColor: 'rgb(34, 197, 94)',
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    tension: 0.4,
                    fill: false,
                    yAxisID: 'y',
                },
                {
                    label: 'Costs',
                    type: 'line',
                    data: monthlyProfitLoss.costs || [],
                    borderColor: 'rgb(239, 68, 68)',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    tension: 0.4,
                    fill: false,
                    yAxisID: 'y',
                },
                {
                    label: 'Profit',
                    data: monthlyProfitLoss.profit || [],
                    backgroundColor: 'rgba(59, 130, 246, 0.7)',
                    borderColor: 'rgb(59, 130, 246)',
                    borderWidth: 1,
                    yAxisID: 'y',
                }
            ]
        };
    }, [data.monthlyProfitLoss]);

    // --- Chart Options (Remain largely the same as they don't depend on data presence for structure) ---

    const commonOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
            },
        },
    };

    const currencyFormatter = (value) => {
        // Safety check included here as well
        if (typeof value !== 'number' || isNaN(value)) return value;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        }).format(value);
    };

    const revenueOptions = {
        ...commonOptions,
        plugins: {
            ...commonOptions.plugins,
            title: {
                display: true,
                text: 'Monthly Revenue Trend (Last 12 Months)',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: currencyFormatter
                }
            }
        },
    };

    const profitMarginOptions = {
        ...commonOptions,
        plugins: {
            ...commonOptions.plugins,
            title: {
                display: true,
                text: 'Profit Margin by Product',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 60,
                ticks: {
                    callback: function(value) {
                        return value + '%';
                    }
                }
            }
        },
    };

    const costBreakdownOptions = {
        ...commonOptions,
        plugins: {
            ...commonOptions.plugins,
            title: {
                display: true,
                text: 'Cost Breakdown Distribution',
            },
            tooltip: {
                ...commonOptions.plugins.tooltip,
                callbacks: {
                    label: function(context) {
                        return `${context.label}: ${context.parsed}%`;
                    }
                }
            }
        },
    };

    const monthlyPLOptions = {
        ...commonOptions,
        plugins: {
            ...commonOptions.plugins,
            title: {
                display: true,
                text: 'Monthly Profit & Loss Overview',
            },
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                beginAtZero: true,
                ticks: {
                    callback: currencyFormatter
                }
            }
        },
    };

    // --- Rendering Logic ---

    // Checks to conditionally render charts based on data availability
    const hasRevenueData = revenueChartData.labels.length > 0 && revenueChartData.datasets[0]?.data.length > 0;
    const hasProfitMarginData = profitMarginChartData.labels.length > 0 && profitMarginChartData.datasets[0]?.data.length > 0;
    const hasCostBreakdownData = costBreakdownChartData.datasets[0]?.data.some(d => d > 0);
    const hasMonthlyPLData = monthlyPLChartData.labels.length > 0 && monthlyPLChartData.datasets.some(ds => ds.data.length > 0);

    // If no charts can be rendered, display a fallback message
    if (!hasRevenueData && !hasProfitMarginData && !hasCostBreakdownData && !hasMonthlyPLData) {
        return (
            <div className="financial-charts no-data-message">
                <p>⚠️ Chart data is currently unavailable or incomplete.</p>
            </div>
        );
    }

    // Panels are conditionally rendered (not just CSS-hidden): Chart.js sizes
    // its canvas from the container's dimensions at mount time, and a
    // container that's `display:none` reports zero size — mounting a chart
    // while hidden and later revealing it leaves the canvas stuck at a wrong
    // (often huge) size, since Chart.js never gets a resize signal from a
    // plain visibility change. Mounting fresh on each tab switch sidesteps
    // that entirely.

    return (
        <div className="financial-charts">
            <div className="chart-tabs" role="tablist">
                {CHART_TABS.map(tab => (
                    <button
                        key={tab.id}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                        className={`chart-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'trends' && (
            <div className="charts-grid">
                {/* Revenue Trend Chart */}
                {hasRevenueData ? (
                    <div className="chart-container">
                        <div className="chart-wrapper">
                            <Line data={revenueChartData} options={revenueOptions} />
                        </div>
                    </div>
                ) : (
                    <div className="chart-container placeholder">Revenue Trend Data Missing</div>
                )}

                {/* Monthly P&L */}
                {hasMonthlyPLData ? (
                    <div className="chart-container full-width">
                        <div className="chart-wrapper">
                            <Bar data={monthlyPLChartData} options={monthlyPLOptions} />
                        </div>
                    </div>
                ) : (
                    <div className="chart-container full-width placeholder">Monthly P&L Data Missing</div>
                )}
            </div>
            )}

            {activeTab === 'costs' && (
            <div className="charts-grid">
                {hasCostBreakdownData ? (
                    <div className="chart-container">
                        <div className="chart-wrapper">
                            <Doughnut data={costBreakdownChartData} options={costBreakdownOptions} />
                        </div>
                    </div>
                ) : (
                    <div className="chart-container placeholder">Cost Breakdown Data Missing</div>
                )}
            </div>
            )}

            {activeTab === 'margins' && (
            <div className="charts-grid">
                {hasProfitMarginData ? (
                    <div className="chart-container">
                        <div className="chart-wrapper">
                            <Bar data={profitMarginChartData} options={profitMarginOptions} />
                        </div>
                    </div>
                ) : (
                    <div className="chart-container placeholder">Profit Margin Data Missing</div>
                )}
            </div>
            )}
        </div>
    );
};

export default FinancialCharts;